// ============================================================================
// src/hooks/useDealDldComparison.ts
// PV-1052 · PR-G — React hook wrapping `getDealDldComparison(dealId)` RPC
//
// PATTERN — mirrors `useDealBySlug` in src/routes/public/DealPage.data.ts
// (discriminated union state + cancellation flag). No SWR / React Query —
// the codebase keeps Supabase calls thin and components own loading/error.
// ============================================================================

import { useEffect, useState } from 'react';

import { getDealDldComparison, type DldComparisonResponse } from '@/lib/db/dld-comparison';

export type DldComparisonState =
  | { status: 'loading'; data: null; error: null }
  | { status: 'success'; data: DldComparisonResponse; error: null }
  | { status: 'error'; data: null; error: Error };

const INITIAL_STATE: DldComparisonState = { status: 'loading', data: null, error: null };

/**
 * Fetches DLD market comparison for a deal id. Returns `loading` until the
 * RPC resolves, then `success` or `error`. Cancellation flag prevents stale
 * setState when dealId changes mid-flight or the component unmounts.
 *
 * `null` dealId keeps the hook idle in `loading` state — the BenchmarkPanel
 * renders a skeleton until the parent passes a real dealId.
 */
export function useDealDldComparison(dealId: string | null): DldComparisonState {
  const [state, setState] = useState<DldComparisonState>(INITIAL_STATE);

  useEffect(() => {
    if (!dealId) {
      setState(INITIAL_STATE);
      return;
    }

    let cancelled = false;
    setState(INITIAL_STATE);

    getDealDldComparison(dealId)
      .then((data) => {
        if (cancelled) return;
        setState({ status: 'success', data, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          data: null,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [dealId]);

  return state;
}
